import { useState } from 'react';
import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import { Response, ErrorResponse, RequestConfig } from '../api-Utils';
import { getSessionProfile } from 'shared/utils/sessionProfileStore';
import useToggle from 'shared/hooks/use-toggle';
import { toast } from 'react-hot-toast';

const useUserService = () => {
    const [users, setUsers] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, toggleLoading] = useToggle();
    
    const id = getSessionProfile()?.id;

    const fetchUsers = async (page, size, status = null) => {
        toggleLoading();
        try {
            const url = status
                ? `${endpoints.users.getUsers}?page=${page}&size=${size}&status=${status}`
                : `${endpoints.users.getUsers}?page=${page}&size=${size}`;
            
            const response = await axiosInstance.get(url);
            const { data } = response.data;

            // Ensure totalRecords is correctly assigned
            setTotalRecords(data.records || 0);

            // Ensure users is always an array
            setUsers(Array.isArray(data.data) ? data.data.map((user) => ({
                id: user.id,
                userName: user.name,
                phoneNumber: user.phoneNumber,
                indexNumber: user.studentNumber,
                email: user.email,
                parishName: user.parishName,
                archdiocese: user.archdiocese,
                communityName: user.communityName,
                status: user.status,
                role: user.role,
                gender: user.gender,
                createdAt: user.createdAt,
            })) : []);

        } catch (error) {
            toast.error('Error fetching users.');
            console.error('Fetch Users Error:', error);
        } finally {
            toggleLoading();
        }
    };

    const loadUserDetails = async (userId) => {
        try {
            toggleLoading();
            const response = await axiosInstance.get(`${endpoints.users.getUser}${userId}`);
            //toast.success(response.data.message || 'User details loaded successfully');
            return response.data.data;
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching user details');
            console.error('Error fetching user details:', error);
            throw error;
        } finally {
            toggleLoading();
        }
    };



    const createUser = async (userDto) => {
        try {
            const response = await axiosInstance.post(endpoints.users.signUp, userDto);
            return response.data;
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    };
        
    const updateUserDetails = async (id, updateUserDetailsDto) => {
        try {
            const response = await axiosInstance.put(`${endpoints.users.update}${id}`, updateUserDetailsDto);
            return response.data;
        } catch (error) {
            console.error('Error updating user details:', error);
            throw error;
        }
    };

    const updateUserStatus = async (userId, currentStatus, onSuccess) => {
        const newStatus = currentStatus === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
        const apiUrl = `${endpoints.users.status}?id=${userId}&status=${newStatus}`;
        
        try {
            const response = await axiosInstance.post(apiUrl);
            if (onSuccess) {
                onSuccess();
            }
            return response.data;
        } catch (error) {
            console.error('Error updating user status:', error);
            throw error;
        }
    };

    const changeUserStatus = async (userId, status) => {
        try {
            const response = await axiosInstance.post(`${endpoints.users.status}?id=${userId}&status=${status}`);
            return response.data;
        } catch (error) {
            console.error('Error changing user status:', error);
            throw error;
        }
    };

    const deleteUser = async (userId, currentStatus, onSuccess) => {
        const newStatus = 'DISABLED';
        const apiUrl = `${endpoints.users.status}?id=${userId}&status=${newStatus}`;
        
        try {
            const response = await axiosInstance.post(apiUrl);
            if (onSuccess) {
                onSuccess();
            }
            return response.data;
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    };

    const rotateUserStatus = async (userId, onSuccess) => {
        try {
            const response = await axiosInstance.post(
                `${endpoints.users.rotateStatus}?id=${userId}`
            );
            toast.success('User status rotated successfully');
            if (onSuccess) {
                onSuccess();
            }
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error rotating user status');
            console.error('Error rotating user status:', error);
            throw error;
        }
    };


    const adminResetPassword = async (userId, registryTenantId = null) => {
        try {
            const headers = registryTenantId
                ? { 'X-TenantID': String(registryTenantId).trim() }
                : {};
            const response = await axiosInstance.post(
                `${endpoints.users.adminResetPassword}?id=${encodeURIComponent(userId)}`,
                {},
                { headers }
            );
            toast.success(
                response.data?.message ||
                    'Password reset to User@2000. The user must change it on next login.'
            );
            return response.data;
        } catch (error) {
            const msg =
                error.response?.data?.message ||
                error.response?.data?.error ||
                'Failed to reset password';
            toast.error(typeof msg === 'string' ? msg : 'Failed to reset password');
            console.error('Error resetting password:', error);
            throw error;
        }
    };

    const adminActivateAccount = async (userId, registryTenantId = null) => {
        try {
            const headers = registryTenantId
                ? { 'X-TenantID': String(registryTenantId).trim() }
                : {};
            const response = await axiosInstance.post(
                `${endpoints.users.adminActivateAccount}?id=${encodeURIComponent(userId)}`,
                {},
                { headers }
            );
            toast.success(
                response.data?.message ||
                    'Account activated with temporary password User@2000. The user must change it on next login.'
            );
            return response.data;
        } catch (error) {
            const msg =
                error.response?.data?.message ||
                error.response?.data?.error ||
                'Failed to activate account';
            toast.error(typeof msg === 'string' ? msg : 'Failed to activate account');
            console.error('Error activating account:', error);
            throw error;
        }
    };

    const changePassword = async (userId, changePasswordDto) => {
        try {
            const response = await axiosInstance.post(
                `${endpoints.users.changePassword}?userId=${userId}`,
                {
                    ...changePasswordDto,
                    confirmedPassword:
                        changePasswordDto.confirmedPassword ?? changePasswordDto.password,
                }
            );
            toast.success(response.data.message || 'Password changed successfully');
            return response.data;
        } catch (error) {
            const msg =
                error.response?.data?.message ||
                error.response?.data?.error ||
                'Failed to change password';
            toast.error(typeof msg === 'string' ? msg : 'Failed to change password');
            console.error('Error changing password:', error);
            throw error;
        }
    };

    /** @param {string} [userId] defaults to logged-in user id from session */
    const uploadUserImage = async (file, userId = id) => {
        if (!userId || !file) {
            toast.error('User ID or file is missing');
            return;
        }
        if (!file.type.startsWith('image/')) {
            toast.error('Please select a valid image file');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File size must be less than 5MB');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
            const url = `${endpoints.files.photo}?id=${encodeURIComponent(String(userId))}`;
            const response = await axiosInstance.put(url, formData, {
                timeout: 30000,
            });
            return response.data;
        } catch (error) {
            console.error('Error updating profile picture:', error);
            toast.error(error.response?.data?.message || 'Error updating profile picture');
            throw error;
        }
    };


    

    return {
        users,
        totalRecords,
        changePassword,
        adminResetPassword,
        adminActivateAccount,
        fetchUsers,
        loadUserDetails,
        createUser,
        updateUserDetails,
        updateUserStatus,
        changeUserStatus,
        deleteUser,
        rotateUserStatus,
        uploadUserImage,
        isLoading,
    };
};

export default useUserService;
